'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../theme/ThemeProvider';

export interface ThemeBreakpoints {
  mobile: number;
  tablet: number;
}

export interface WindowDimensions {
  width: number;
  height: number;
}

export interface UseWindowDimensionsResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  windowDimensions: WindowDimensions;
}

const getWindowDimensions = (): WindowDimensions => {
  if (globalThis.window === undefined) {
    return { width: 0, height: 0 };
  }
  return { width: globalThis.window.innerWidth, height: globalThis.window.innerHeight };
};

/**
 * Hook that tracks viewport dimensions and returns responsive breakpoint flags.
 *
 * Breakpoint logic (matches Shapermint convention):
 *   mobile:  width < breakpoints.mobile   (default 640)
 *   tablet:  mobile <= width < breakpoints.tablet (default 1024)
 *   desktop: width >= breakpoints.tablet
 *
 * Breakpoints are read from the theme unless explicitly overridden.
 */
export function useWindowDimensions(
  breakpointOverrides?: Partial<ThemeBreakpoints>,
): UseWindowDimensionsResult {
  const theme = useTheme();

  const mobileBreakpoint = breakpointOverrides?.mobile ?? theme.mediaQueries.mobile;
  const tabletBreakpoint = breakpointOverrides?.tablet ?? theme.mediaQueries.tablet;

  const [dimensions, setDimensions] = useState<WindowDimensions>(getWindowDimensions);

  const handleResize = useCallback(() => {
    setDimensions(getWindowDimensions());
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const { width } = dimensions;

  return {
    isMobile: width > 0 && width < mobileBreakpoint,
    isTablet: width >= mobileBreakpoint && width < tabletBreakpoint,
    isDesktop: width >= tabletBreakpoint,
    windowDimensions: dimensions,
  };
}
